/**
 * The API contract, as this client sees it.
 *
 * These types used to live in a shared workspace package. The frontend is now
 * a separate repository, so its slice is inlined here — ten symbols out of a
 * package the backend uses in ninety files. Copying is deliberate: publishing
 * and versioning a 250-line package to share an enum and a pagination envelope
 * costs more than it saves.
 *
 * These must stay in step with `packages/shared` in the API repository. What
 * keeps them honest is the API itself: a mismatch shows up as a failing
 * request, not a type error. Nothing here is enforced across the wire.
 *
 * Note what is *not* here, and never was: doctype names. Every `can('Employee
 * Grievance', …)` call passes a bare string matched against the server's
 * registry, so renaming a doctype in the API breaks the permission gate
 * silently. That was true in the monorepo too — the shared package never
 * exported those to the web.
 */

/** Verbs the permission engine understands (API: `PermAction`). */
export enum PermAction {
  Read = 'read',
  Write = 'write',
  Create = 'create',
  Delete = 'delete',
  Submit = 'submit',
  Cancel = 'cancel',
  Amend = 'amend',
  Print = 'print',
  Email = 'email',
  Export = 'export',
  Import = 'import',
  Share = 'share',
  Report = 'report',
  SetUserPermissions = 'set_user_permissions',
}

/** Document lifecycle (API: `DocStatus`). Kept because slips and claims read it. */
export enum DocStatus {
  Draft = 0,
  Submitted = 1,
  Cancelled = 2,
}

export enum ExportFormat {
  CSV = 'csv',
  XLSX = 'xlsx',
  JSON = 'json',
}

/** Envelope every list endpoint returns. */
export interface Paginated<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface ApiErrorBody {
  statusCode: number;
  error: string;
  message: string | string[];
  /** i18n key so the client can render Arabic without server-side locale. */
  code?: string;
  path?: string;
  timestamp?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SessionUser {
  id: string;
  /** Null for an employee's app account, which signs in by mobile number. */
  email: string | null;
  fullName: string;
  language: string;
  roles: string[];
  /** Companies this user may operate in; empty = all (System Manager). */
  companies: string[];
  defaultCompany?: string | null;
  employeeId?: string | null;
  isAdmin: boolean;
  tenant?: string | null;
}

/** Resolved grants, keyed by doctype name. */
export interface PermissionMap {
  [doctype: string]: Partial<Record<PermAction, boolean>>;
}

export interface ReportColumn {
  key: string;
  label: string;
  /** Rendering + export hint. */
  type:
    | 'text'
    | 'number'
    | 'currency'
    | 'date'
    | 'datetime'
    | 'link'
    | 'check'
    | 'duration'
    | 'percent';
  width?: number;
  align?: 'left' | 'right' | 'center';
  doctype?: string;
  precision?: number;
}

export interface ReportFilterDef {
  key: string;
  label: string;
  type: 'text' | 'select' | 'link' | 'date' | 'date_range' | 'number' | 'check';
  required?: boolean;
  options?: string[];
  doctype?: string;
  default?: unknown;
}

/** Option row for a link field, from `/link-options/:doctype`. */
export interface LinkOption {
  value: string;
  label: string;
  labelAr?: string;
  /**
   * The short code people know a record by, where the doctype declares one —
   * an employee's number. Shown before the name: `1193 · عمر سامح فتحى يونس`.
   */
  code?: string | null;
}

export interface ReportResult {
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  /** Optional aggregate row rendered pinned at the bottom. */
  totals?: Record<string, unknown>;
  message?: string;
}

/** A doctype the data screen can import into or export, from `GET /data/doctypes`. */
export interface DataDoctype {
  name: string;
  label: string;
  labelAr?: string;
  module: string;
  canImport: boolean;
  canExport: boolean;
}

export type ImportFieldType =
  | 'string'
  | 'integer'
  | 'number'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'json'
  | 'link';

/** A field a sheet column can be mapped onto. */
export interface ImportField {
  name: string;
  label: string;
  type: ImportFieldType;
  required: boolean;
  maxLength?: number;
  /** The doctype a `link` field points at. */
  link?: string;
  options?: string[];
}

/** `GET /data/import/:doctype/template` — the columns a doctype takes. */
export interface ImportTemplate {
  headers: string[];
  sample: Record<string, string>;
  fields: ImportField[];
}

/** `POST /data/import/:doctype/preview` — the file read back, nothing written. */
export interface ImportPreview {
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
  /** Sheet header → field name, or null where nothing matched. */
  suggestedMap: Record<string, string | null>;
  fields: ImportField[];
}

export type ImportMode = 'insert' | 'update' | 'upsert';

export type ImportRunStatus = 'pending' | 'validating' | 'success' | 'partial_success' | 'failed';

export interface ImportRowError {
  row: number;
  field?: string;
  value?: string;
  message: string;
}

/** One import attempt, dry run or not. */
export interface ImportRun {
  id: string;
  doctype: string;
  fileName: string;
  mode: ImportMode;
  status: ImportRunStatus;
  dryRun: boolean;
  totalRows: number;
  successRows: number;
  errorRows: number;
  errors: ImportRowError[] | null;
  createdAt: string;
}

/** The four sheets of the organisation setup workbook, in import order. */
export type SetupSheetKey = 'branches' | 'departments' | 'designations' | 'employees';

/** Masters an import will create (or created) because the Employees sheet named them. */
export interface SetupAutoCreate {
  doctype: string;
  names: string[];
}

/** `POST /data/setup/preview` — the workbook read and validated, nothing written. */
export interface SetupPreview {
  ok: boolean;
  sheets: {
    key: SetupSheetKey;
    label: string;
    found: boolean;
    rows: number;
    create: number;
    update: number;
    unknownColumns: string[];
    errors: ImportRowError[];
  }[];
  autoCreate: SetupAutoCreate[];
  totalErrors: number;
}

/** `POST /data/setup/import` — what the workbook wrote, sheet by sheet. */
export interface SetupResult {
  ok: boolean;
  run: ImportRun;
  sheets: {
    key: SetupSheetKey;
    label: string;
    found: boolean;
    rows: number;
    created: number;
    updated: number;
    errors: ImportRowError[];
  }[];
  autoCreated: SetupAutoCreate[];
}

/**
 * An agent's standing, as the API works it out. `pending` is a code issued and
 * not yet used; `online` a heartbeat within the last 15 minutes; `offline` an
 * enrolled agent that has gone quiet; `disabled` is switched off from here.
 */
export type SyncAgentStatus = 'pending' | 'online' | 'offline' | 'revoked' | 'disabled';

/** One terminal as the agent last reported it in its heartbeat. */
export interface AgentDeviceReport {
  /** The terminal serial — `Device.deviceId`, not the row id. */
  deviceId: string;
  lastAttemptAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
  failureCount: number;
  /** Reads refused because other software held the terminal. */
  busyCount: number;
  /** Punches the terminal is holding; every read downloads them all. */
  logCount: number | null;
  /** 'YYYY-MM-DD HH:mm:ss', as the terminal itself shows it. */
  terminalClock: string | null;
  /** Punches waiting in the agent's outbox for this terminal. */
  queued: number;
}

/** A program on a branch or head-office machine that reads terminals over the LAN. */
export interface SyncAgent {
  id: string;
  companyId: string;
  name: string;
  status: SyncAgentStatus;
  /** No heartbeat for 15 minutes. */
  silent: boolean;
  enrolmentExpiresAt: string | null;
  enrolledAt: string | null;
  revokedAt: string | null;
  tokenIssuedAt: string | null;
  rotateRequestedAt: string | null;
  pollIntervalSeconds: number;
  maxConcurrentReads: number;
  holdUpdates: boolean;
  isActive: boolean;
  version: string | null;
  nodeVersion: string | null;
  hostname: string | null;
  os: string | null;
  startedAt: string | null;
  lastSeenAt: string | null;
  /** The machine's clock against the server's, in seconds. */
  agentClockSkewSeconds: number | null;
  /** Punches waiting on the machine to be uploaded. */
  outboxDepth: number | null;
  lastPassMs: number | null;
  lastHeartbeat: {
    devices: AgentDeviceReport[];
    updateFailed?: string | null;
    outboxFull?: boolean;
  } | null;
  deviceCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * `POST /checkins/agents` and `…/reissue-code` — the enrolment code in clear,
 * exactly once. The API keeps only its hash.
 */
export interface AgentCodeIssued {
  agent: SyncAgent;
  code: string;
  expiresAt: string;
}

/** `GET /checkins/agents/release` — the agent build this server hands out. */
export interface AgentRelease {
  available: boolean;
  version: string | null;
  file: string | null;
  size: number | null;
  publishedAt: string | null;
}
